import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import PackSheet, { isUsPackSheet, auPageCount } from './PackSheet';
import { useRegion } from '../region';
import CoverPage, { blankCover } from './CoverPage';
import ProjectEditModal from './ProjectEditModal';
import LibraryPicker from './LibraryPicker';
import { HighlightRects, TypeBoxesStatic, TextBoxesStatic } from './ProposalStudio';
import { defaultSheet } from '../datasheet';
import Modal from './Modal';
import { FileUpload } from './ImageUpload';
import { PROJECT_STATUSES, statusMeta } from '../projectStatus';
import { fillOppNames, projectMatches, oppNumberOf } from '../projectOpp';
import { exportProjectPack } from '../exportPack';

function coverPageOf(items, region) {
  if ((items || []).some(it => it.type === 'archive')) return null;
  const total = 1 + (items || []).reduce((n, it) => n + (isUsPackSheet(it.data, region) ? 0 : auPageCount(it.data)), 0);
  // US kit sheets vary in length; the count is only shown when it is exact.
  if ((items || []).some(it => isUsPackSheet(it.data, region))) return null;
  return 'Page 1 of ' + total;
}

export default function Projects({ toast, me, canAll, canAssign, goStudio }) {
  const region = useRegion();
  const meId = me && me.id;
  const [projects, setProjects] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ name: '', client: '', cover_sheet: blankCover(region) });
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const [archivePicker, setArchivePicker] = useState(false);
  const [memberPicker, setMemberPicker] = useState(false);
  const [newModal, setNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmDelProj, setConfirmDelProj] = useState(null);
  const [viewAll, setViewAll] = useState(false);
  const [pq, setPq] = useState('');
  const [users, setUsers] = useState([]);
  const exportRef = useRef(null);
  const saveTimer = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  const usersById = {};
  users.forEach(u => { usersById[u.id] = u; });

  const load = useCallback(async () => {
    const r = await api.listProjects(viewAll && canAll ? { all: true } : {});
    const items = r.items || [];
    setProjects(items);
    fillOppNames(items, byId => {
      setProjects(ps => ps.map(p => (byId[p.id] ? { ...p, ...byId[p.id] } : p)));
      setSel(s => (s && byId[s.id] ? { ...s, ...byId[s.id] } : s));
    });
    return items;
  }, [viewAll, canAll]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { (async () => { try { const r = await api.listProjectUsers(); setUsers(r.items || []); } catch (e) { if (e?.response?.status !== 403) console.error('Failed to load project users', e); } })(); }, []);

  useEffect(() => {
    if (sel) setForm({ name: sel.name || '', client: sel.client || '', cover_sheet: sel.cover_sheet || blankCover(region) });
  }, [sel && sel.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleSave = useCallback(() => {
    if (!sel) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const f = formRef.current;
      const fresh = await api.updateProject(sel.id, { name: f.name, client: f.client, cover_sheet: f.cover_sheet });
      setProjects(ps => ps.map(p => p.id === fresh.id ? fresh : p));
    }, 450);
  }, [sel]);

  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); scheduleSave(); };
  const setCover = (k, v) => { setForm(f => ({ ...f, cover_sheet: { ...f.cover_sheet, [k]: v } })); scheduleSave(); };

  async function createProject() {
    const name = newName.trim();
    if (!name) return;
    const row = await api.createProject({ name, cover_sheet: blankCover(region), items: [] });
    setNewModal(false); setNewName('');
    await load(); setSel(row); toast('Project created');
  }
  async function removeProjectConfirmed() {
    const p = confirmDelProj; setConfirmDelProj(null);
    await api.deleteProject(p.id); if (sel && sel.id === p.id) setSel(null); await load(); toast('Deleted');
  }

  async function mutateItems(nextItems, msg) {
    const fresh = await api.updateProject(sel.id, { items: nextItems });
    setSel(fresh); setProjects(ps => ps.map(p => p.id === fresh.id ? fresh : p));
    if (msg) toast(msg);
  }
  async function addItems(products) {
    const items = [...((sel && sel.items) || [])];
    products.forEach(p => items.push({ id: 'it_' + Math.random().toString(36).slice(2, 9), name: p.name, code: p.code, data: p.data || {}, updatedAt: new Date().toISOString() }));
    await mutateItems(items, products.length + ' datasheet(s) added'); setPicker(false);
  }
  async function addArchiveItems(files) {
    const items = [...((sel && sel.items) || [])];
    files.forEach(f => items.push({ id: 'it_' + Math.random().toString(36).slice(2, 9), type: 'archive', legacyId: f.id, name: f.name, contentType: f.content_type || '', updatedAt: new Date().toISOString() }));
    await mutateItems(items, files.length + ' archive file(s) added'); setArchivePicker(false);
  }
  async function removeItem(id) { await mutateItems(((sel && sel.items) || []).filter(it => it.id !== id)); }

  // Grab-and-drop reordering of the pack list (drag starts from the ⠿ handle
  // so the corner-note inputs keep normal text selection).
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [armedIdx, setArmedIdx] = useState(null);
  function dropReorder(to) {
    const from = dragIdx;
    setDragIdx(null); setOverIdx(null); setArmedIdx(null);
    if (from == null || to == null || from === to) return;
    const next = [...((sel && sel.items) || [])];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    mutateItems(next, 'Order updated');
  }
  const dragProps = (i) => ({
    draggable: armedIdx === i,
    onDragStart: (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIdx !== i) setOverIdx(i); },
    onDrop: (e) => { e.preventDefault(); dropReorder(i); },
    onDragEnd: () => { setDragIdx(null); setOverIdx(null); setArmedIdx(null); },
  });
  const rowCls = (i) => 'proj-item-row' + (dragIdx === i ? ' dragging' : '') + (overIdx === i && dragIdx != null && dragIdx !== i ? ' drag-over' : '');

  // Direct file upload: file goes into the shared Archive AND is added to this project.
  async function uploadDatasheetFiles(files) {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const next = [...((sel && sel.items) || [])];
      for (const f of Array.from(files)) {
        const meta = await api.uploadLegacy(f, (f.name || '').replace(/\.[^.]+$/, ''), 'project-upload');
        next.push({ id: 'it_' + Math.random().toString(36).slice(2, 9), type: 'archive', legacyId: meta.id, name: meta.name, contentType: meta.content_type || '', updatedAt: new Date().toISOString() });
      }
      await mutateItems(next, files.length + ' datasheet(s) uploaded — added to this project and saved to the Archive');
    } catch (e) { toast('Upload failed: ' + ((e.response && e.response.data && e.response.data.detail) || e.message), true); }
    setBusy(false);
  }

  // Upload a datasheet PDF straight into a flagged "datasheet required" slot.
  async function uploadMissing(m, file) {
    if (!file) return;
    setBusy(true);
    try {
      const meta = await api.uploadLegacy(file, (m.code || m.name || file.name || '').replace(/\.[^.]+$/, ''), 'quote-missing');
      const next = [...((sel && sel.items) || []), {
        id: 'it_' + Math.random().toString(36).slice(2, 9), type: 'archive', legacyId: meta.id,
        name: meta.name, contentType: meta.content_type || '', fulfills: m.code || m.name,
        updatedAt: new Date().toISOString(),
      }];
      const remaining = ((sel && sel.missing_datasheets) || []).filter(x => x !== m);
      const fresh = await api.updateProject(sel.id, { items: next, missing_datasheets: remaining });
      setSel(fresh); setProjects(ps => ps.map(p => p.id === fresh.id ? fresh : p));
      toast('Datasheet uploaded — "' + (m.name || m.code) + '" slot filled');
    } catch (e) { toast('Upload failed: ' + ((e.response && e.response.data && e.response.data.detail) || e.message), true); }
    setBusy(false);
  }

  // Per-project edits — operate ONLY on this project's private copy (item.data),
  // never the source datasheet in Core/Customer/Personal.
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  function openEdit(it) { setEditId(it.id); setEditData({ ...defaultSheet(), ...(it.data || {}) }); }
  async function saveEdit() {
    const next = ((sel && sel.items) || []).map(x => x.id === editId
      ? { ...x, data: editData, name: editData.name || x.name, code: editData.code || x.code, updatedAt: new Date().toISOString() }
      : x);
    setEditId(null); setEditData(null);
    await mutateItems(next, 'Project datasheet updated (source unchanged)');
  }
  async function setItemCorner(it, value) {
    if ((it.data || {}).corner === value) return;
    const next = ((sel && sel.items) || []).map(x => x.id === it.id ? { ...x, data: { ...(x.data || {}), corner: value } } : x);
    await mutateItems(next);
  }

  async function changeMember(action, userId) {
    const fresh = await api.updateProjectMembers(sel.id, action, userId);
    setSel(fresh); setProjects(ps => ps.map(p => p.id === fresh.id ? fresh : p));
    toast(action === 'add' ? 'Member added' : 'Member removed');
  }

  async function exportPdf() {
    if (!sel || !exportRef.current) return;
    setBusy(true);
    try {
      await exportProjectPack(exportRef.current, (sel.items || []), form.name, (w) => toast(w, true));
      toast('PDF exported');
      api.logEvent('proposal.export', 'project', sel.id, { name: form.name, items: (sel.items || []).length });
    } catch (e) { toast('Export failed: ' + e.message, true); }
    setBusy(false);
  }

  const items = (sel && sel.items) || [];
  const missing = (sel && sel.missing_datasheets) || [];
  const cover = form.cover_sheet || {};
  const members = (sel && sel.members) || [];
  const canManageMembers = sel && canAssign && (canAll || sel.owner_id === meId);
  const userLabel = (id) => { const u = usersById[id]; return u ? (u.name || u.email) : id; };
  const shown = projects.filter(p => projectMatches(p, pq));

  return (
    <div className="projects-layout" data-testid="projects-view">
      <aside className="proj-side">
        <div className="proj-side-head">
          <span>My Projects</span>
          <button className="btn btn-primary btn-sm" data-testid="project-new" onClick={() => { setNewName(''); setNewModal(true); }}>＋ New</button>
        </div>
        {canAll && (
          <div className="proj-scope">
            <select className="input" data-testid="project-scope-select" value={viewAll ? 'all' : 'mine'} onChange={e => { setViewAll(e.target.value === 'all'); setSel(null); }}>
              <option value="mine">My projects (assigned to me)</option>
              <option value="all">All projects (admin view)</option>
            </select>
          </div>
        )}
        <div className="proj-search">
          <input className="input" data-testid="project-search" value={pq} onChange={e => setPq(e.target.value)}
            placeholder="Search name, opportunity # or client…" />
        </div>
        <div className="proj-list">
          {projects.length === 0 ? <div className="empty-note">{viewAll ? 'No projects exist yet.' : 'No projects assigned to you yet. Create one, or ask an owner to add you.'}</div> :
            shown.length === 0 ? <div className="empty-note" data-testid="project-search-empty">Nothing matched “{pq}”.</div> :
            shown.map(p => (
              <div key={p.id} className={'proj-item' + (sel && sel.id === p.id ? ' active' : '')} data-testid={'project-' + p.id} onClick={() => setSel(p)}>
                <div className="nm">{p.name} <span className={'proj-status-pill ' + statusMeta(p.status).cls} data-testid={'project-status-pill-' + p.id}>{statusMeta(p.status).label}</span>
                  <div className="cd">
                    {oppNumberOf(p) && p.name !== oppNumberOf(p) && <span className="proj-opp-num" data-testid={'project-opp-' + p.id}>{oppNumberOf(p)}</span>}
                    {(p.items || []).length} datasheet(s) · {(p.members || []).length} member(s)
                  </div>
                </div>
                {(canAll || p.owner_id === meId) && <button className="btn btn-sm btn-danger" onClick={e => { e.stopPropagation(); setConfirmDelProj(p); }} title="Delete">✕</button>}
              </div>
            ))}
        </div>
      </aside>

      <main className="proj-main">
        {!sel ? (
          <div className="empty-hero"><h2>Select or create a project</h2><p>Projects are private to their team. Assign colleagues under “Team” to give them access. Bundle datasheets with a cover sheet and export a single client-facing PDF pack.</p></div>
        ) : (
          <>
            <div className="proj-toolbar">
              <input className="input proj-name" data-testid="project-name-input" value={form.name} onChange={e => setField('name', e.target.value)} />
              {oppNumberOf(sel) && form.name !== oppNumberOf(sel) && (
                <span className="proj-opp-num lg" data-testid="project-opp-number" title="NetSuite opportunity — search by this number or the name">{oppNumberOf(sel)}</span>
              )}
              <select className={'select proj-status-select ' + statusMeta(sel.status).cls} data-testid="project-status-select"
                value={sel.status || 'ongoing'} title="Project status"
                onChange={async e => {
                  const fresh = await api.updateProject(sel.id, { status: e.target.value });
                  setSel(fresh); setProjects(ps => ps.map(p => p.id === fresh.id ? fresh : p));
                  toast('Status set to ' + statusMeta(fresh.status).label);
                }}>
                {PROJECT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <div className="proj-actions">
                <button className="btn" data-testid="project-add-items" onClick={() => setPicker(true)}>＋ Add Datasheets</button>
                <button className="btn" data-testid="project-add-archive" onClick={() => setArchivePicker(true)}>＋ Add from Archive</button>
                <FileUpload testid="project-upload-datasheets" label={busy ? 'Uploading…' : '＋ Upload Datasheets'} disabled={busy} multiple accept="image/*,application/pdf" onFiles={uploadDatasheetFiles} />
                <button className="btn" data-testid="project-open-studio" onClick={() => goStudio && goStudio(sel.id)}>Proposal Studio →</button>
                <button className="btn btn-primary" data-testid="project-export-pdf" onClick={exportPdf} disabled={busy || items.length === 0}>{busy ? 'Exporting…' : 'Export Combined PDF'}</button>
              </div>
            </div>

            <div className="proj-team" data-testid="project-team">
              <div className="team-head">
                <h4>Team · who can see this project</h4>
                {canManageMembers && <button className="btn btn-sm" data-testid="project-add-member" onClick={() => setMemberPicker(true)}>＋ Add member</button>}
              </div>
              <div className="member-chips">
                {members.length === 0 ? <span className="empty-note" style={{ padding: 0 }}>No members.</span> :
                  members.map(id => (
                    <span className="member-chip" key={id} data-testid={'member-' + id}>
                      {userLabel(id)}{sel.owner_id === id ? <em> (owner)</em> : null}
                      {canManageMembers && sel.owner_id !== id && <button className="chip-x" data-testid={'member-remove-' + id} onClick={() => changeMember('remove', id)} title="Remove">✕</button>}
                    </span>
                  ))}
              </div>
            </div>

            <div className="cover-editor">
              <h4>Cover Sheet</h4>
              <div className="grid2">
                <L label="Client (internal)"><input className="input" data-testid="cover-client" value={form.client || ''} onChange={e => setField('client', e.target.value)} /></L>
                <L label="Date Presented"><input className="input" type="date" data-testid="cover-date" value={cover.datePresented || ''} onChange={e => setCover('datePresented', e.target.value)} /></L>
                <L label="Revision"><input className="input" data-testid="cover-revision" value={cover.revision || ''} onChange={e => setCover('revision', e.target.value)} /></L>
                <L label="Prepared By"><input className="input" data-testid="cover-preparedby" value={cover.preparedBy || ''} onChange={e => setCover('preparedBy', e.target.value)} /></L>
                <L label="Prepared By Email"><input className="input" data-testid="cover-email" value={cover.preparedByEmail || ''} onChange={e => setCover('preparedByEmail', e.target.value)} /></L>
                <L label="Address"><input className="input" data-testid="cover-address" value={cover.address || ''} onChange={e => setCover('address', e.target.value)} /></L>
                <L label="Telephone"><input className="input" data-testid="cover-telephone" value={cover.telephone || ''} onChange={e => setCover('telephone', e.target.value)} /></L>
              </div>
            </div>

            {missing.length > 0 && (
              <div className="proj-missing" data-testid="project-missing-datasheets">
                <h4>Datasheets required ({missing.length}) — quoted items with no datasheet in this pack</h4>
                {missing.map((m, mi) => (
                  <div className="proj-missing-row" key={(m.code || m.name || '') + mi} data-testid={'missing-ds-' + mi}>
                    <span className="proj-missing-flag">REQUIRED</span>
                    <span className="nm">{m.name || m.code}{m.code && m.code !== m.name ? <em>{m.code}</em> : null}</span>
                    <FileUpload className="btn btn-sm" testid={'missing-ds-upload-' + mi} label={busy ? 'Uploading…' : 'Upload datasheet'} disabled={busy} accept="image/*,application/pdf" onFiles={files => uploadMissing(m, files[0])} />
                  </div>
                ))}
              </div>
            )}

            <div className="proj-items">
              <h4>Datasheets in this pack ({items.length})</h4>
              {items.length === 0 ? <div className="empty-note">No datasheets yet — click “＋ Add Datasheets”.</div> :
                items.map((it, i) => it.type === 'archive' ? (
                  <div className={rowCls(i)} key={it.id} data-testid={'project-item-' + it.id} {...dragProps(i)}>
                    <span className="drag-handle" data-testid={'project-item-drag-' + it.id} title="Drag to reorder" onMouseDown={() => setArmedIdx(i)} onMouseUp={() => setArmedIdx(null)}>⠿</span>
                    <span className="nm">{it.name || '(Untitled)'} <em>· Archive file</em></span>
                    <button className="btn btn-sm btn-danger" onClick={() => removeItem(it.id)}>Remove</button>
                  </div>
                ) : (
                  <div className={rowCls(i)} key={it.id} data-testid={'project-item-' + it.id} {...dragProps(i)}>
                    <span className="drag-handle" data-testid={'project-item-drag-' + it.id} title="Drag to reorder" onMouseDown={() => setArmedIdx(i)} onMouseUp={() => setArmedIdx(null)}>⠿</span>
                    <span className="nm">{it.name || '(Untitled)'} {it.code ? <em>{it.code}</em> : null}</span>
                    <input className="input proj-item-corner" data-testid={'project-item-corner-' + it.id} placeholder="Type / corner note for this project…" defaultValue={(it.data || {}).corner || ''} onBlur={e => setItemCorner(it, e.target.value)} />
                    <button className="btn btn-sm" data-testid={'project-item-edit-' + it.id} onClick={() => openEdit(it)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => removeItem(it.id)}>Remove</button>
                  </div>
                ))}
            </div>

            <div className="export-host" ref={exportRef} aria-hidden="true">
              <CoverPage name={form.name} cover={cover} pageOf={coverPageOf(items, region)} />
              {items.filter(it => it.type !== 'archive').map(it => (
                <div data-export-page key={it.id}>
                  <PackSheet data={it.data} region={region} pageId={'pp_' + it.id}
                    overlay={<><HighlightRects rects={(it.highlights || {})['1']} /><TypeBoxesStatic boxes={(it.typeBoxes || {})['1']} /><TextBoxesStatic boxes={(it.textBoxes || {})['1']} /></>}
                    overlay2={<><HighlightRects rects={(it.highlights || {})['2']} /><TypeBoxesStatic boxes={(it.typeBoxes || {})['2']} /><TextBoxesStatic boxes={(it.textBoxes || {})['2']} /></>} />
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {picker && <LibraryPicker onClose={() => setPicker(false)} onAdd={addItems} />}
      {archivePicker && <ArchivePicker onClose={() => setArchivePicker(false)} onAdd={addArchiveItems} />}

      {editId && editData && (
        <ProjectEditModal editData={editData} setEditData={setEditData}
          onCancel={() => { setEditId(null); setEditData(null); }} onSave={saveEdit} />
      )}

      {memberPicker && (
        <MemberPicker users={users} current={members} onClose={() => setMemberPicker(false)} onAdd={id => { changeMember('add', id); setMemberPicker(false); }} />
      )}

      <Modal open={newModal} title="New project" onClose={() => setNewModal(false)} testid="new-project-modal"
        actions={<>
          <button className="btn" onClick={() => setNewModal(false)}>Cancel</button>
          <button className="btn btn-primary" data-testid="new-project-create" disabled={!newName.trim()} onClick={createProject}>Create</button>
        </>}>
        <label className="field-label">Project name</label>
        <input className="input" data-testid="new-project-name" autoFocus value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) createProject(); }} placeholder="e.g. Acme Office Fitout" />
      </Modal>

      <Modal open={!!confirmDelProj} title="Delete project?" onClose={() => setConfirmDelProj(null)} testid="confirm-delete-project-modal"
        actions={<>
          <button className="btn" onClick={() => setConfirmDelProj(null)}>Cancel</button>
          <button className="btn btn-danger-solid" data-testid="confirm-delete-project-btn" onClick={removeProjectConfirmed}>Delete</button>
        </>}>
        <p>This will permanently delete <strong>{confirmDelProj && confirmDelProj.name}</strong> and its pack contents.</p>
      </Modal>
    </div>
  );
}

function L({ label, children }) { return <div className="field"><label className="field-label">{label}</label>{children}</div>; }

function ArchivePicker({ onClose, onAdd }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState({});
  const [q, setQ] = useState('');
  useEffect(() => { (async () => { try { const r = await api.listLegacy(''); setItems(r.items || []); } catch (e) { console.error('Failed to load archive', e); } })(); }, []);
  const filtered = items.filter(f => !q || (f.name || '').toLowerCase().includes(q.toLowerCase()));
  const chosen = items.filter(f => sel[f.id]);
  return (
    <div className="modal-overlay" data-testid="archive-picker" onMouseDown={onClose}>
      <div className="modal" onMouseDown={e => e.stopPropagation()}>
        <h3>Add from the Archive</h3>
        <p className="modal-hint">Archive PDFs and images are merged straight into the exported pack, after the datasheets before them in the list.</p>
        <input className="input" placeholder="Search the archive…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="picker-list">
          {filtered.length === 0 ? <div className="empty-note">The archive is empty.</div> :
            filtered.map(f => (
              <label key={f.id} className={'picker-row' + (sel[f.id] ? ' on' : '')} data-testid={'archive-pick-' + f.id}>
                <input type="checkbox" checked={!!sel[f.id]} onChange={e => setSel(s => ({ ...s, [f.id]: e.target.checked }))} />
                <span>{f.name || '(Untitled)'}</span>
              </label>
            ))}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" data-testid="archive-picker-add" disabled={chosen.length === 0} onClick={() => onAdd(chosen)}>Add {chosen.length || ''}</button>
        </div>
      </div>
    </div>
  );
}

function MemberPicker({ users, current, onClose, onAdd }) {
  const [q, setQ] = useState('');
  const avail = users.filter(u => current.indexOf(u.id) < 0 && (!q || ((u.name || '') + (u.email || '')).toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="modal-overlay" data-testid="member-picker" onMouseDown={onClose}>
      <div className="modal" onMouseDown={e => e.stopPropagation()}>
        <h3>Add a team member</h3>
        <input className="input" placeholder="Search people…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="picker-list">
          {avail.length === 0 ? <div className="empty-note">No more users to add.</div> :
            avail.map(u => (
              <button className="picker-row" key={u.id} data-testid={'member-pick-' + u.id} onClick={() => onAdd(u.id)}>
                <span>{u.name || u.email}<em> · {u.email}</em></span>
              </button>
            ))}
        </div>
        <div className="modal-actions"><button className="btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}
